import { NATS_KV_BUCKETS } from "./platform-streams.js";
import { accountInfoSubjects, compileNatsGrantSubjects, compileVerbOnEveryInbox, compileVerbOnEveryStream } from "./stream-verbs.js";
//#region src/nats-standing-grants.ts
/**
* The recipient shapes of the standing grants. Each shape is one class of credential and the grant
* family it receives.
*
* | shape | recipient | helper of `event-subjects.ts` |
* |---|---|---|
* | `svc` | a shared service principal (event-server, archivers), kind `svc` | `agentConsumerGrantSubjects(org, 'svc', id)` |
* | `human` | a human principal, kind `human` | `agentConsumerGrantSubjects(org, 'human', id)` |
* | `agent-stream` | an agent or worker principal, kind `agent`: its own stream | the first 13 subjects of `agentConsumerGrantSubjects(org, 'agent', id)` |
* | `agent-lease` | an agent or worker principal: its own one-mind lease key | `agentLeaseKvGrantSubjects(org, id)` |
* | `unit-lease` | a compute unit or agent host: its org's lease keys | `orgScopedLeaseKvGrantSubjects(org)` |
* | `unit-revocations` | a compute unit or agent host: the revocation read | `agentRevocationsKvReadGrantSubjects()` |
* | `agent-revocations-self` | an agent or worker principal: its own revocation key `<org>.<principalId>` (D-17) | none; core-api's agent map composes it (D-24) |
* | `org-inbox-post` | an `agent` principal on its own-stream grant (core-api composes it for type `agent` alone; D-12 named every principal): the post into any inbox of the org | none; the second entry of `agentPublishSubjects(org, 'agent', id)` |
* | `agent-author` | an `agent` principal on its own-stream grant (core-api composes it for type `agent` alone): the append into its own subtree `org.<org>.p.<kind>.<id>.>` | none; the first entry of `agentPublishSubjects(org, 'agent', id)` |
*
* The org transport `org.<org>.>` of a unit (`orgTransportGrantSubjects`) is core NATS on no grant
* resource, so it is no standing row. Frozen.
*/
const NATS_STANDING_GRANT_SHAPES = Object.freeze([
	"svc",
	"human",
	"agent-stream",
	"agent-lease",
	"unit-lease",
	"unit-revocations",
	"agent-revocations-self",
	"org-inbox-post",
	"agent-author"
]);
/** A frozen standing row. */
function standing(row) {
	return Object.freeze({
		...row,
		resource: Object.freeze({ ...row.resource })
	});
}
/** The own principal stream. */
const OWN = { type: "own" };
/** Every resource of the type in reach. */
const ALL = { type: "all" };
/** The lease bucket, `COORDINATION_KV_BUCKETS.agentLeases`. */
const LEASES = {
	type: "bucket",
	name: "AGENT_LEASES"
};
/** The event-server leader lock bucket. */
const EVENT_SERVER_LEADER = {
	type: "bucket",
	name: "event-server-leader"
};
/** The S3 archiver leader lock bucket. */
const S3_ARCHIVER_LEADER = {
	type: "bucket",
	name: "s3-archiver-leader"
};
/** The kill-switch bucket. */
const REVOCATIONS = {
	type: "bucket",
	name: "AGENT_REVOCATIONS"
};
/** One frozen `nats_kv_bucket` standing row. */
function kv(shape, verb, resource, confinement, dialect, accountInfo) {
	return standing({
		shape,
		type: "nats_kv_bucket",
		verb,
		resource,
		confinement,
		dialect,
		accountInfo
	});
}
/**
* Every standing grant, in compile order. A shape's rows compile in table order, and the first
* occurrence of a subject wins.
*
* | shape | type | verb | resource | confinement | dialect | probe |
* |---|---|---|---|---|---|---|
* | `svc` | `nats_stream` | read | all | stream | plain | no |
* | `svc` | `nats_kv_bucket` | read, write | `AGENT_LEASES` | stream | plain | no |
* | `svc` | `nats_kv_bucket` | read, write | `event-server-leader` | stream | plain | no |
* | `svc` | `nats_kv_bucket` | read, write | `s3-archiver-leader` | stream | plain | no |
* | `human` | `nats_stream` | read | own | stream | qualified | no |
* | `agent-stream` | `nats_stream` | read | own | stream | dual | no |
* | `agent-lease` | `nats_kv_bucket` | read | `AGENT_LEASES` | stream | dual | yes |
* | `agent-lease` | `nats_kv_bucket` | write | `AGENT_LEASES` | principal | dual | no |
* | `unit-lease` | `nats_kv_bucket` | read | `AGENT_LEASES` | stream | dual | yes |
* | `unit-lease` | `nats_kv_bucket` | write | `AGENT_LEASES` | org | dual | no |
* | `unit-revocations` | `nats_kv_bucket` | read | `AGENT_REVOCATIONS` | stream | dual | no |
* | `agent-revocations-self` | `nats_kv_bucket` | read | `AGENT_REVOCATIONS` | principal | dual | no |
* | `org-inbox-post` | `nats_inbox` | post | all | org | plain | no |
* | `agent-author` | `nats_stream` | append | own | principal | plain | no |
*
* The `svc` KV rows are the three `COORDINATION_KV_RW_BUCKET_NAMES` buckets and never
* `AGENT_REVOCATIONS`: a shared service never writes the kill switch, and no row writes it. The lease
* and `unit-revocations` reads are stream-level, the documented residual of today (R-009, R-038): a
* subject cannot confine a body-form get. The `agent-revocations-self` read is key-confined: the
* account probe, the subject-form direct get and the filtered watch create of the one key
* `<orgId>.<principalId>`, with no `STREAM.INFO` (R-007). So a credential that holds this row and no
* stream-level revocation read (`unit-revocations`) must bind with
* `views.kv('AGENT_REVOCATIONS', { bindOnly: true, allow_direct: true })`, which sends no
* `STREAM.INFO`. The default bind of `createRevocationKvBinding` in `@zion/sdk-node` sends
* `$JS.API.STREAM.INFO.KV_AGENT_REVOCATIONS`, and the broker refuses it on this row alone. The
* `org-inbox-post` row compiles to the core NATS subject `org.<orgId>.p.*.*.inbox.>`, so its dialect
* changes nothing. That subject matches no team request subject of `nats_request_endpoint`, which
* sits under `org.<orgId>.rq.` (R-042), so the row sends no request. The `agent-author` row compiles
* to the core NATS subject `org.<orgId>.p.<streamKind>.<principalId>.>`, the recipient's own subtree
* with its inbox facet, so its dialect changes nothing either. It holds no `_INBOX.>`. core-api's
* `natsTransportFor` decides which principal gets `_INBOX.>` from the DEV fallback. An `agent` on its
* own-stream grant gets it from a `nats_request_endpoint:serve` row, or from an enroll-time column
* that names it (author shapes D-4, D-9). With no `streamKind` in the context it compiles to nothing. A grant row never carries `append`
* (`isNatsGrantVerbReserved`), so this row is the one way to the own-subtree append in the model.
* The array and every row are frozen.
*/
const NATS_STANDING_GRANTS = Object.freeze([
	standing({
		shape: "svc",
		type: "nats_stream",
		verb: "read",
		resource: ALL,
		confinement: "stream",
		dialect: "plain",
		accountInfo: false
	}),
	kv("svc", "read", LEASES, "stream", "plain", false),
	kv("svc", "write", LEASES, "stream", "plain", false),
	kv("svc", "read", EVENT_SERVER_LEADER, "stream", "plain", false),
	kv("svc", "write", EVENT_SERVER_LEADER, "stream", "plain", false),
	kv("svc", "read", S3_ARCHIVER_LEADER, "stream", "plain", false),
	kv("svc", "write", S3_ARCHIVER_LEADER, "stream", "plain", false),
	standing({
		shape: "human",
		type: "nats_stream",
		verb: "read",
		resource: OWN,
		confinement: "stream",
		dialect: "qualified",
		accountInfo: false
	}),
	standing({
		shape: "agent-stream",
		type: "nats_stream",
		verb: "read",
		resource: OWN,
		confinement: "stream",
		dialect: "dual",
		accountInfo: false
	}),
	kv("agent-lease", "read", LEASES, "stream", "dual", true),
	kv("agent-lease", "write", LEASES, "principal", "dual", false),
	kv("unit-lease", "read", LEASES, "stream", "dual", true),
	kv("unit-lease", "write", LEASES, "org", "dual", false),
	kv("unit-revocations", "read", REVOCATIONS, "stream", "dual", false),
	kv("agent-revocations-self", "read", REVOCATIONS, "principal", "dual", false),
	standing({
		shape: "org-inbox-post",
		type: "nats_inbox",
		verb: "post",
		resource: ALL,
		confinement: "org",
		dialect: "plain",
		accountInfo: false
	}),
	standing({
		shape: "agent-author",
		type: "nats_stream",
		verb: "append",
		resource: OWN,
		confinement: "principal",
		dialect: "plain",
		accountInfo: false
	})
]);
/** The compile options of one row for one context. A missing id becomes `''`, which the token grammar refuses. */
function optionsFor(row, context) {
	const orgId = context.orgId ?? "";
	return row.confinement === "principal" ? {
		orgId,
		confinement: "principal",
		principalId: context.principalId ?? "",
		dialect: row.dialect
	} : {
		orgId,
		confinement: row.confinement,
		dialect: row.dialect
	};
}
/** The publish subjects of one row. */
function rowSubjects(row, context) {
	const options = optionsFor(row, context);
	switch (row.resource.type) {
		case "all": return row.type === "nats_inbox" ? compileVerbOnEveryInbox(row.verb, options.orgId) : compileVerbOnEveryStream(row.verb, row.dialect);
		case "own": return compileNatsGrantSubjects(row.type, {
			type: "principal",
			slug: context.principalId ?? "",
			streamKind: context.streamKind
		}, row.verb, options).publish;
		case "bucket": {
			const name = row.resource.name;
			return Object.values(NATS_KV_BUCKETS).filter((bucket) => bucket.name === name).flatMap((bucket) => compileNatsGrantSubjects(row.type, {
				type: "bucket",
				bucket
			}, row.verb, options).publish);
		}
	}
}
/**
* Compiles every standing grant of one recipient shape for one recipient.
*
* Callers: the grant helpers of `event-subjects.ts`; core-api's `nats-permissions.ts`, which composes
* a principal's platform-minted grant from the shapes `svc`, `human`, `agent-stream`, `agent-lease`,
* `agent-revocations-self`, `unit-lease` and `unit-revocations` (`platformMintedConsumerGrant`,
* `platformMintedUnitGrant`), and from stage S2 of 2026-10-06-nats-standing-author-shapes also
* `agent-author` and `org-inbox-post` for a principal of type `agent` on its own-stream grant
* (author shapes D-3, R-005). A caller that
* compiles `agent-author` passes the recipient's `streamKind`. A credential that holds
* `agent-revocations-self` and no stream-level revocation read binds by the rule of
* {@link NATS_STANDING_GRANTS}.
*
* The answer is the publish subjects of the shape's rows in table order, each row's account probe
* first when it carries one, with the first occurrence of a subject kept and later copies dropped.
* `subscribe` is empty. An unknown shape answers two empty lists. Both lists are frozen.
*
* Errors: it throws the token error of `agentStreamName`, `assertValidStreamOrgId` or
* `assertValidStreamPrincipalId` when a row reads an `orgId` or `principalId` that is absent or fails
* the token grammar. The `svc` and `unit-revocations` shapes read neither, so they never throw. The
* `agent-author` shape with no valid `streamKind` reads neither and answers two empty lists, so it
* never throws; with a valid `streamKind` it throws for an absent or malformed `orgId` or
* `principalId`. No side effects.
*/
function compileStandingGrants(shape, context) {
	const publish = [];
	const seen = /* @__PURE__ */ new Set();
	for (const row of NATS_STANDING_GRANTS) {
		if (row.shape !== shape) continue;
		const subjects = row.accountInfo ? [...accountInfoSubjects(row.dialect), ...rowSubjects(row, context)] : rowSubjects(row, context);
		for (const subject of subjects) {
			if (seen.has(subject)) continue;
			seen.add(subject);
			publish.push(subject);
		}
	}
	return {
		publish: Object.freeze(publish),
		subscribe: Object.freeze([])
	};
}
//#endregion
export { NATS_STANDING_GRANTS, NATS_STANDING_GRANT_SHAPES, compileStandingGrants };

//# sourceMappingURL=nats-standing-grants.js.map